import { config } from '@/config';
import { NoteOut } from '@/services/noteGoApi/noteGoApiSchemas';
import { SECOND_BRAIN_NOTES_LIMIT } from '../config';
import { SecondBrainChat, SecondBrainContextNote, SecondBrainMessage } from '../types';
import { getTagNameOrNull } from '@/utils/tagUtils';
import { errorReporter } from '@/services/errorReportingService';

const SECOND_BRAIN_ENDPOINT = '/second-brain/chats';

interface RawMessage {
  id?: string | number;
  role?: string;
  author?: string;
  content?: string;
  text?: string;
  parts?: Array<string | { text?: string; content?: string }>;
  created_at?: string;
  createdAt?: string;
}

interface RawContextNote {
  id?: string | number;
  note_id?: string | number;
  title?: string;
  name?: string;
  summary?: string;
  content?: string;
  created_at?: string;
  createdAt?: string;
  tags?: Array<string | { name?: string; id?: string | number; label?: string }>;
  thumbnail_url?: string | null;
}

interface RawChatRecord {
  id?: string | number;
  chat_id?: string | number;
  uuid?: string;
  _id?: string;
  messages?: RawMessage[];
  history?: RawMessage[];
  question?: string;
  prompt?: string;
  input?: string;
  user_message?: string;
  answer?: string;
  response?: string;
  output?: string;
  model_message?: string;
  context_notes?: RawContextNote[];
  notes?: RawContextNote[];
  tags?: Array<string | { name?: string; label?: string }>;
  model?: string;
  model_version?: string;
  selected_model?: string;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
}

interface RawChatsResponse {
  chats?: RawChatRecord[];
}

type StreamUpdateCallback = (fullText: string) => void;

type StreamChunkCallback = (chunk: string) => void;

export interface FetchSecondBrainChatsOptions {
  token: string;
}

export interface StartSecondBrainChatStreamingParams {
  token: string;
  question: string;
  tags: string[];
  notes: NoteOut[];
  model?: string | null;
  signal?: AbortSignal;
  onUpdate?: StreamUpdateCallback;
  onChunk?: StreamChunkCallback;
}

export interface StartSecondBrainChatStreamingResult {
  chatId?: string;
  finalText: string;
  chat?: SecondBrainChat;
}

const normalizeMessage = (message: RawMessage): SecondBrainMessage => {
  const rawRole = message?.role ?? message?.author ?? 'assistant';
  const role = rawRole === 'model' ? 'assistant' : rawRole === 'human' ? 'user' : rawRole;
  const content =
    typeof message?.content === 'string'
      ? message.content
      : Array.isArray(message?.parts)
        ? message.parts
            .map((part) =>
              typeof part === 'string'
                ? part
                : typeof part?.text === 'string'
                  ? part.text
                  : typeof part?.content === 'string'
                    ? part.content
                    : '',
            )
            .join('')
        : (message?.text ?? '');

  // Validate role
  const allowedRoles: SecondBrainMessage['role'][] = ['user', 'assistant', 'system'];
  let finalRole: SecondBrainMessage['role'] = 'assistant';

  if (content && role && allowedRoles.includes(role as SecondBrainMessage['role'])) {
    finalRole = role as SecondBrainMessage['role'];
  } else if (content && role && !allowedRoles.includes(role as SecondBrainMessage['role'])) {
    // Debug: Unexpected role value from API - not critical but worth monitoring
    errorReporter.debug(`Unexpected role value: "${role}". Defaulting to "assistant".`, {
      feature: 'secondBrain',
      operation: 'normalizeMessage',
    });
  }

  return {
    id: message?.id ? String(message.id) : undefined,
    role: finalRole,
    content: content ?? '',
    createdAt: (message?.created_at ?? message?.createdAt ?? null) as string | null,
  };
};

const normalizeContextNote = (note: RawContextNote): SecondBrainContextNote | null => {
  const id = note?.id ?? note?.note_id;
  if (!id) {
    return null;
  }

  const title = note?.title ?? note?.name ?? null;
  const summary = note?.summary ?? note?.content ?? null;
  const createdAt = (note?.created_at ?? note?.createdAt ?? null) as string | null;
  const tags = Array.isArray(note?.tags)
    ? note.tags.map((tag) => getTagNameOrNull(tag)).filter((tag): tag is string => Boolean(tag))
    : undefined;

  return {
    id: String(id),
    title: title ? String(title) : null,
    summary: summary ? String(summary) : null,
    createdAt,
    tags,
    thumbnailUrl: note?.thumbnail_url ?? null,
  };
};

const buildMessagesFromSimpleFields = (raw: RawChatRecord | null | undefined): SecondBrainMessage[] => {
  if (!raw) {
    return [];
  }

  const messages: SecondBrainMessage[] = [];
  const question = raw.question ?? raw.prompt ?? raw.input ?? raw.user_message;
  const answer = raw.answer ?? raw.response ?? raw.output ?? raw.model_message;

  if (question) {
    messages.push({ role: 'user', content: String(question), createdAt: raw.created_at ?? raw.createdAt ?? null });
  }

  if (answer) {
    messages.push({ role: 'assistant', content: String(answer), createdAt: raw.updated_at ?? raw.updatedAt ?? null });
  }

  return messages;
};

const normalizeChatRecord = (raw: RawChatRecord | null | undefined): SecondBrainChat | null => {
  if (!raw) {
    return null;
  }

  const id = raw.id ?? raw.chat_id ?? raw.uuid ?? raw._id;
  if (!id) {
    return null;
  }

  const rawMessages = Array.isArray(raw.messages) ? raw.messages : Array.isArray(raw.history) ? raw.history : undefined;

  const messages = rawMessages?.length
    ? rawMessages.map((message) => normalizeMessage(message)).filter((message) => message.content)
    : buildMessagesFromSimpleFields(raw);

  const contextArray = Array.isArray(raw.context_notes) ? raw.context_notes : Array.isArray(raw.notes) ? raw.notes : [];

  const contextNotes = contextArray
    .map((note) => normalizeContextNote(note))
    .filter((note): note is SecondBrainContextNote => Boolean(note));

  const questionMessage = messages.find((message) => message.role === 'user');
  const assistantMessages = messages.filter((message) => message.role === 'assistant');
  const answerMessage = assistantMessages.length > 0 ? assistantMessages[assistantMessages.length - 1] : undefined;

  const tags = Array.isArray(raw.tags)
    ? raw.tags.map((tag) => getTagNameOrNull(tag)).filter((tag): tag is string => Boolean(tag))
    : [];

  return {
    id: String(id),
    tags,
    model: raw.model ?? raw.model_version ?? raw.selected_model ?? null,
    createdAt: (raw.created_at ?? raw.createdAt ?? null) as string | null,
    updatedAt: (raw.updated_at ?? raw.updatedAt ?? null) as string | null,
    messages,
    contextNotes: contextNotes.length > 0 ? contextNotes : undefined,
    question: questionMessage?.content,
    answer: answerMessage?.content,
  };
};

const isJson = (value: string): boolean => {
  const trimmed = value.trim();
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) {
    return false;
  }
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return false;
  }
};

const extractTextFromChunk = (
  chunk: string,
): { text: string; potentialJsonFragments: string[]; streamError?: string } => {
  let collectedText = '';
  let streamError: string | undefined;
  const jsonCandidates: string[] = [];
  const lines = chunk
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  for (const line of lines) {
    if (line === '[DONE]') {
      continue;
    }

    const processedLine = line.startsWith('data:') ? line.replace(/^data:\s*/, '') : line;
    if (!processedLine) {
      continue;
    }

    if (isJson(processedLine)) {
      jsonCandidates.push(processedLine);
      try {
        const parsed = JSON.parse(processedLine);
        if (typeof parsed === 'string') {
          collectedText += parsed;
        } else if (typeof parsed?.error === 'string') {
          streamError = parsed.error;
        } else if (typeof parsed?.content === 'string') {
          collectedText += parsed.content;
        } else if (Array.isArray(parsed?.parts)) {
          collectedText += parsed.parts
            .map((part: unknown) => {
              if (typeof part === 'string') {
                return part;
              }
              const candidate = part as { text?: unknown; content?: unknown } | null | undefined;
              return candidate?.text ?? candidate?.content ?? '';
            })
            .join('');
        } else if (typeof parsed?.delta === 'string') {
          collectedText += parsed.delta;
        } else if (parsed?.delta?.content) {
          collectedText += parsed.delta.content;
        } else if (typeof parsed?.answer === 'string') {
          collectedText += parsed.answer;
        }
      } catch {
        collectedText += processedLine;
      }
    } else {
      collectedText += processedLine;
    }
  }

  return { text: collectedText, potentialJsonFragments: jsonCandidates, streamError };
};

const resolveFinalPayload = (
  rawBuffer: string,
  fallback: string,
): { chatId?: string; chat?: SecondBrainChat; finalText: string } => {
  const trimmed = rawBuffer.trim();
  if (!trimmed) {
    return { finalText: fallback };
  }

  const candidates = trimmed
    .split(/\r?\n/)
    .map((line) => line.replace(/^data:\s*/, '').trim())
    .filter((line) => line && line !== '[DONE]');

  const tryParseJson = (candidate: string) => {
    try {
      const parsed = JSON.parse(candidate);
      const potentialChat = normalizeChatRecord(parsed.chat ?? parsed);
      const finalText = parsed.answer ?? parsed.response ?? parsed.content ?? potentialChat?.answer ?? fallback ?? '';
      const chatId = parsed.chat_id ?? parsed.id ?? potentialChat?.id;
      return { chatId, chat: potentialChat ?? undefined, finalText };
    } catch {
      return null;
    }
  };

  for (const candidate of candidates.reverse()) {
    const parsed = tryParseJson(candidate);
    if (parsed) {
      return parsed;
    }
  }

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    const parsed = tryParseJson(trimmed);
    if (parsed) {
      return parsed;
    }
  }

  return { finalText: fallback || trimmed };
};

// Shape of the `context_notes` entries POSTed to the backend — must match the
// snake_case field names on `ContextNote` (app/schemas/second_brain.py) exactly.
// The backend has no camelCase alias generator, so a camelCase key here is
// silently dropped (Pydantic's default `extra="ignore"`), not a validation error.
interface OutboundContextNote {
  id: string;
  title: string | null;
  summary: string | null;
  created_at: string | null;
  tags: string[];
  thumbnail_url: string | null;
}

const mapNotesToContext = (notes: NoteOut[]): OutboundContextNote[] =>
  notes.slice(0, SECOND_BRAIN_NOTES_LIMIT).map((note) => ({
    id: note.id,
    title: note.title ?? null,
    summary: note.summary ?? null,
    created_at: note.created_at ?? null,
    tags: note.tags ?? [],
    thumbnail_url: note.thumbnail_list_url ?? note.thumbnail_url ?? null,
  }));

export const fetchSecondBrainChats = async ({ token }: FetchSecondBrainChatsOptions): Promise<SecondBrainChat[]> => {
  const response = await fetch(`${config.apiUrl}${SECOND_BRAIN_ENDPOINT}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('BACKEND_NOT_AVAILABLE');
    }
    throw new Error('Failed to load Second Brain chats');
  }

  const payload: RawChatRecord[] | RawChatsResponse = await response.json();
  const rawChats = Array.isArray(payload) ? payload : Array.isArray(payload?.chats) ? payload.chats : [];

  return rawChats
    .map((rawChat) => normalizeChatRecord(rawChat))
    .filter((chat): chat is SecondBrainChat => Boolean(chat));
};

export const startSecondBrainChatStreaming = async ({
  token,
  question,
  tags,
  notes,
  model,
  signal,
  onUpdate,
  onChunk,
}: StartSecondBrainChatStreamingParams): Promise<StartSecondBrainChatStreamingResult> => {
  const payload = {
    question,
    tags,
    note_ids: notes.map((note) => note.id),
    context_notes: mapNotesToContext(notes),
    model,
  };

  const response = await fetch(`${config.apiUrl}${SECOND_BRAIN_ENDPOINT}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('BACKEND_NOT_AVAILABLE');
    }
    const errorPayload = await response.text();
    throw new Error(errorPayload || 'Failed to start Second Brain chat');
  }

  const hintedChatId = response.headers.get('x-second-brain-chat-id') ?? undefined;

  if (!response.body) {
    const data = await response.json();
    const normalizedChat = normalizeChatRecord(data.chat ?? data);
    const finalText =
      data.answer ?? data.response ?? normalizedChat?.answer ?? normalizedChat?.messages?.at(-1)?.content ?? '';
    if (!finalText && typeof data?.error === 'string') {
      throw new Error(`SECOND_BRAIN_GENERATION_FAILED: ${data.error}`);
    }
    if (finalText && onUpdate) {
      onUpdate(finalText);
    }
    return {
      chatId: hintedChatId ?? normalizedChat?.id ?? data.chat_id ?? data.id,
      finalText: finalText ?? '',
      chat: normalizedChat ?? undefined,
    };
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let aggregatedText = '';
  let rawBuffer = '';
  let streamError: string | undefined;

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    const chunk = decoder.decode(value, { stream: true });
    rawBuffer += chunk;
    const { text, potentialJsonFragments, streamError: chunkError } = extractTextFromChunk(chunk);

    if (text) {
      aggregatedText += text;
      onUpdate?.(aggregatedText);
    }

    if (chunkError) {
      streamError = chunkError;
    }

    potentialJsonFragments.forEach((fragment) => onChunk?.(fragment));
  }

  const finalChunk = decoder.decode();
  if (finalChunk) {
    rawBuffer += finalChunk;
  }

  const result = resolveFinalPayload(rawBuffer, aggregatedText);
  if (result.finalText && result.finalText !== aggregatedText) {
    aggregatedText = result.finalText;
    onUpdate?.(aggregatedText);
  }

  if (!aggregatedText && streamError) {
    throw new Error(`SECOND_BRAIN_GENERATION_FAILED: ${streamError}`);
  }

  return {
    chatId: result.chatId ?? hintedChatId,
    finalText: aggregatedText,
    chat: result.chat,
  };
};
